<template>
  <property-report-card
    title="Lease Terms"
    description="Monitor the best lease term offered across all units."
    :printing-mode="printingMode"
    :is-empty="!unitsFound(filteredProperties)"
  >
    <q-separator />
    <q-card-section>
      <div id="intercom-lease-terms-content" style="overflow-x: auto">
        <div style="width: 100%; height: 300px">
          <bar class="chart" :data="chartData" :options="chartOptions" :plugins="chartPlugins" />
        </div>
      </div>
    </q-card-section>
    <q-separator v-if="!printingMode" />
    <q-card-section v-if="!printingMode">
      <q-card flat>
        <div v-if="userSelection" class="flex justify-between q-mb-md">
          <div class="text-bold q-mb-md q-my-auto">
            Units With {{ userSelection.term }} Month Lease Term
            <q-icon name="close" class="q-ml-md cursor-pointer" @click="closePanel" />
          </div>
          <q-btn flat no-caps class="q-my-auto" @click="exportCSV">
            <q-icon name="download" class="q-mr-sm" size="medium" />Download
          </q-btn>
        </div>
        <div
          v-if="!tableUnits.length"
          class="full-width text-center justify-center flex align-center"
        >
          <q-icon name="info" size="18px" color="accent" class="q-mr-sm q-my-auto" />
          Click on a bar in the chart above to see the units that make it up.
        </div>
        <unit-details-table
          v-else
          :rows="tableUnits"
          :expanded-unit-history="expandedUnitHistory"
          :date-range="dateRange"
        />
      </q-card>
    </q-card-section>
  </property-report-card>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import type { ExpandedUnitHistory } from '@/types/propertyReports'
import { Bar } from 'vue-chartjs'
import type { ChartOptions } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { unitsFound, unitTypeColor } from '@/utils'

import type { PropertyDetailsResponse, Availability, AvailabilityPeriod, Price } from 'common'

import UnitDetailsTable, { exportCSV } from './UnitDetailsTable.vue'
import PropertyReportCard from '@/components/PropertyReportCard.vue'

export default {
  name: 'PropertyCompareLeaseTerms',
  components: { PropertyReportCard, Bar, UnitDetailsTable },
  props: {
    filteredProperties: {
      type: Array<{
        prop: PropertyDetailsResponse
        units: {
          unit: Availability
          lastHistory: Price
          period: AvailabilityPeriod
        }[]
      }>,
      required: true
    },
    expandedUnitHistory: {
      type: Object as PropType<ExpandedUnitHistory>,
      required: true
    },
    dateRange: {
      type: Object as PropType<{ from: Date; to: Date }>,
      required: true
    },
    printingMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      userSelection: null as {
        term: number
        beds: number | null
      } | null,
      chartPlugins: [ChartDataLabels]
    }
  },
  computed: {
    generateData() {
      const termUnits: {
        [months: string]: {
          unit: Availability
          lastHistory: Price
          period: AvailabilityPeriod
          prop: PropertyDetailsResponse
        }[]
      } = {}
      for (const prop of this.filteredProperties) {
        for (const unit of prop.units) {
          const duration = unit.lastHistory.lease_term
          if (duration) {
            if (!termUnits[duration]) {
              termUnits[duration] = []
            }
            termUnits[duration].push({
              unit: unit.unit,
              lastHistory: unit.lastHistory,
              period: unit.period,
              prop: prop.prop
            })
          }
        }
      }
      return Object.keys(termUnits).map((term) => ({
        term: Number(term),
        units: termUnits[term]
      }))
    },
    relevantBeds() {
      const beds = [
        ...new Set(
          this.filteredProperties.flatMap(({ units }) => units.map(({ unit }) => unit.bed))
        )
      ]
      beds.sort()
      return beds as number[]
    },
    chartData() {
      const selectedWeek = this.userSelection?.term ?? ''
      const selectedBeds = this.userSelection?.beds ?? -1
      const datasets: {
        label: string
        borderColor: string
        backgroundColor: (ctx: { dataIndex: number; datasetIndex: number }) => string
        data: number[]
        borderWidth: number
        datalabels: {
          align: string
          anchor: string
          font: { weight: string }
          formatter: (value: string) => string
        }
      }[] = []
      this.relevantBeds.forEach((bed) => {
        const dataset = {
          label: `${bed}BR`,
          borderColor: unitTypeColor(bed),
          backgroundColor: (ctx: { dataIndex: number; datasetIndex: number }) => {
            if (
              selectedWeek === this.generateData[ctx.dataIndex]?.term &&
              ctx.datasetIndex === this.relevantBeds.indexOf(selectedBeds)
            ) {
              return unitTypeColor(bed)
            }
            return unitTypeColor(bed, 0.6)
          },
          data: this.generateData.map((col) => col.units.filter((_) => _.unit.bed === bed).length),
          borderWidth: 1,
          borderRadius: 4,
          barPercentage: 0.9,
          maxBarThickness: 150,
          datalabels: {
            align: 'center',
            anchor: 'center',
            font: { weight: 'bold' },
            color: this.$q.dark.isActive ? 'white' : undefined,
            formatter: (value: string) => value || ''
          }
        }
        datasets.push(dataset)
      })
      if (datasets.length) {
        datasets[datasets.length - 1].datalabels.align = 'top'
        datasets[datasets.length - 1].datalabels.anchor = 'end'
      }
      return {
        labels: this.generateData.map((data) => data.term),
        datasets
      }
    },
    tableUnits() {
      if (this.userSelection?.term) {
        return (
          this.generateData
            .find((_) => _.term === this.userSelection?.term)
            ?.units.filter(({ unit }) => {
              let pass = true
              if (this.userSelection?.beds != null) {
                pass = pass && this.userSelection.beds === unit.bed
              }
              return pass
            }) || []
        )
      }
      return []
    },
    chartOptions() {
      return {
        onClick: (_: unknown, clickedElements) => {
          if (this.printingMode) {
            return
          }
          if (clickedElements.length === 0) {
            return
          }
          const { dataIndex, datasetIndex } = clickedElements[0].element.$context
          this.userSelection = {
            term: this.generateData[dataIndex].term,
            beds: this.relevantBeds[datasetIndex]
          }
        },
        maintainAspectRatio: false,
        animation: false,
        plugins: {
          legend: {
            onClick: () => null,
            display: true,
            position: 'top',
            labels: {
              boxWidth: 10,
              boxHeight: 10,
              font: {
                size: 12
              }
            }
          }
        },
        scales: {
          x: {
            stacked: true
          },
          y: {
            stacked: true,
            grace: '25%'
          }
        }
      } as ChartOptions<'bar'>
    }
  },
  watch: {
    dateRange() {
      this.userSelection = null
    }
  },
  methods: {
    unitsFound,
    exportCSV() {
      exportCSV(this.tableUnits, 'lease-terms', this.dateRange)
      this.$tracker.exported('lease_terms')
    },
    closePanel() {
      this.userSelection = null
    }
  }
}
</script>
